import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Modal from "@/components/base/Modal";
import NumberInput from "@/components/base/NumberInput";
import {
  inputClass,
  labelClass,
  ghostButtonClass,
  primaryButtonClass,
} from "@/components/base/fieldStyles";
import type { PurchaseToStockInput } from "@/store/AppStore";
import type { ExpenseCategory, Product, Wallet } from "@/types";
import { formatCOP } from "@/utils/format";

const WALLETS: Wallet[] = ["Bancolombia", "Daviplata", "Nequi", "Caja Social", "Efectivo"];

// Categorías de una COMPRA de inventario. Los fondos (caja menor, reinversión,
// etc.) NO van aquí: eso es contabilidad interna. Una compra se clasifica por su
// motivo: reposición, encargo o aumento de stock.
const PURCHASE_CATEGORIES: ExpenseCategory[] = [
  "Compra por aumento de stock",
  "Compra por reposición",
  "Compra por encargo",
  "Gasto operativo",
];

const PAYMENTS: { key: PurchaseToStockInput["payment"]; label: string; icon: string; hint: string }[] = [
  {
    key: "pendiente",
    label: "Dejar pendiente",
    icon: "ri-time-line",
    hint: "La compra queda por pagar y la pagas luego desde los fondos.",
  },
  {
    key: "caja",
    label: "Pagar con Caja Menor",
    icon: "ri-safe-2-line",
    hint: "Se paga de una vez con el fondo Caja Menor (si alcanza).",
  },
];

interface PurchaseToStockModalProps {
  open: boolean;
  products: Product[];
  cajaBalance?: number;
  initialProductId?: string;
  onClose: () => void;
  onSubmit: (input: PurchaseToStockInput) => void;
}

// Compra del admin para ingresar a stock: registra la compra (Contabilidad) y
// suma las unidades a Oficina en una sola acción, eligiendo el origen de pago.
const PurchaseToStockModal = ({
  open,
  products,
  cajaBalance = 0,
  initialProductId,
  onClose,
  onSubmit,
}: PurchaseToStockModalProps) => {
  const navigate = useNavigate();
  const [productId, setProductId] = useState("");
  const [qty, setQty] = useState(1);
  const [unitCost, setUnitCost] = useState(0);
  const [category, setCategory] = useState<ExpenseCategory>("Compra por aumento de stock");
  const [wallet, setWallet] = useState<Wallet>("Efectivo");
  const [payment, setPayment] = useState<PurchaseToStockInput["payment"]>("pendiente");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    const initial =
      (initialProductId && products.find((p) => p.id === initialProductId)) || products[0];
    setProductId(initial?.id ?? "");
    setQty(1);
    setUnitCost(initial?.costBase ?? 0);
    setCategory("Compra por aumento de stock");
    setWallet("Efectivo");
    setPayment("pendiente");
    setError("");
  }, [open, products, initialProductId]);

  const product = useMemo(
    () => products.find((p) => p.id === productId) ?? null,
    [products, productId]
  );

  useEffect(() => {
    if (product) setUnitCost(product.costBase);
  }, [product]);

  const total = (Number(unitCost) || 0) * (Number(qty) || 0);
  const selectedPayment = PAYMENTS.find((p) => p.key === payment);
  const cajaShort = payment === "caja" && total > cajaBalance;

  const submit = () => {
    if (!product) {
      setError("Selecciona un producto.");
      return;
    }
    if ((Number(qty) || 0) <= 0) {
      setError("La cantidad debe ser mayor a cero.");
      return;
    }
    if (cajaShort) {
      setError("La Caja Menor no tiene fondos suficientes para esta compra.");
      return;
    }
    onSubmit({
      productId: product.id,
      qty: Number(qty) || 0,
      unitCost: Number(unitCost) || 0,
      category,
      wallet,
      payment,
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Comprar para stock"
      icon="ri-shopping-cart-2-line"
      size="lg"
      footer={
        <>
          <button onClick={onClose} className={ghostButtonClass}>
            Cancelar
          </button>
          <button onClick={submit} className={primaryButtonClass}>
            <i className="ri-add-circle-line" /> Comprar e ingresar a Oficina
          </button>
        </>
      }
    >
      <div className="rounded-md bg-secondary-500/10 border border-secondary-500/25 px-3 py-2.5 mb-4 flex items-start gap-2">
        <i className="ri-information-line text-secondary-200 mt-0.5" />
        <p className="text-[11px] text-foreground-700">
          En una sola acción se registra la compra en contabilidad y las unidades entran al{" "}
          <span className="font-semibold text-foreground-900">stock de Oficina</span>.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <label className={labelClass}>Producto</label>
          <select value={productId} onChange={(e) => setProductId(e.target.value)} className={inputClass}>
            {products.length === 0 && <option value="">Sin productos en catálogo</option>}
            {products.map((p) => (
              <option key={p.id} value={p.id} className="bg-background-100">
                {p.sku} · {p.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className={labelClass}>Cantidad (unidades)</label>
          <NumberInput min={1} value={qty} onChange={setQty} className={inputClass} />
        </div>

        <div>
          <label className={labelClass}>Costo base por unidad (COP)</label>
          <NumberInput min={0} value={unitCost} onChange={setUnitCost} className={inputClass} />
        </div>

        <div>
          <label className={labelClass}>Tipo de compra</label>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value as ExpenseCategory)}
            className={inputClass}
          >
            {PURCHASE_CATEGORIES.map((c) => (
              <option key={c} value={c} className="bg-background-100">
                {c}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className={labelClass}>Billetera</label>
          <select
            value={wallet}
            onChange={(e) => setWallet(e.target.value as Wallet)}
            className={inputClass}
          >
            {WALLETS.map((w) => (
              <option key={w} value={w} className="bg-background-100">
                {w}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="mt-4">
        <label className={labelClass}>Método de pago</label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {PAYMENTS.map((p) => (
            <button
              key={p.key}
              type="button"
              onClick={() => setPayment(p.key)}
              className={`rounded-md border px-3 py-2.5 text-left cursor-pointer transition-colors ${
                payment === p.key
                  ? "border-accent-500/50 bg-accent-500/10"
                  : "border-background-300/50 hover:border-accent-500/30"
              }`}
            >
              <span className="flex items-center gap-1.5 text-[11px] font-semibold text-foreground-900">
                <i className={`${p.icon} text-accent-200`} /> {p.label}
              </span>
            </button>
          ))}
        </div>
        {selectedPayment && (
          <p className="text-[10px] text-foreground-500 mt-1.5">{selectedPayment.hint}</p>
        )}
      </div>

      {payment === "caja" && (
        <div className="mt-2 rounded-md bg-background-200/40 border border-background-300/40 px-3 py-2 flex items-center justify-between">
          <span className="text-[11px] text-foreground-600">Saldo en Caja Menor</span>
          <span className={`text-xs font-bold ${cajaShort ? "text-primary-200" : "text-accent-200"}`}>
            {formatCOP(cajaBalance)}
          </span>
        </div>
      )}

      {/* Aviso flotante: caja menor sin fondos → pedir préstamo a Juank. */}
      {cajaShort && (
        <div className="mt-3 rounded-lg bg-primary-500/12 border border-primary-400/40 px-4 py-3 animate-fade-up">
          <div className="flex items-start gap-2.5">
            <span className="w-8 h-8 rounded-md bg-primary-500/25 text-primary-100 flex items-center justify-center shrink-0">
              <i className="ri-alert-line" />
            </span>
            <div className="min-w-0">
              <p className="text-xs font-bold text-foreground-950">La Caja Menor no tiene fondos suficientes</p>
              <p className="text-[11px] text-foreground-700 mt-0.5">
                Tienes {formatCOP(cajaBalance)} y la compra es de {formatCOP(total)}. Debes pedir un
                préstamo a Juank para cubrirla; ese dinero entra a la Caja Menor.
              </p>
              <button
                type="button"
                onClick={() => {
                  onClose();
                  navigate("/admin/cartera");
                }}
                className="mt-2 px-3 py-2 rounded-md bg-primary-500 hover:bg-primary-600 text-foreground-950 text-[11px] font-semibold whitespace-nowrap cursor-pointer inline-flex items-center gap-1.5"
              >
                <i className="ri-exchange-dollar-line" /> Ir a pedir préstamo a Juank
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="mt-4 rounded-md bg-background-200/40 border border-background-300/40 px-4 py-3 flex items-center justify-between">
        <span className="text-xs text-foreground-600">Total de la compra</span>
        <span className="text-lg font-heading font-extrabold text-accent-200">{formatCOP(total)}</span>
      </div>

      {error && (
        <p className="text-[11px] text-primary-200 mt-3 flex items-center gap-1.5">
          <i className="ri-error-warning-line" /> {error}
        </p>
      )}
    </Modal>
  );
};

export default PurchaseToStockModal;